import { supabase } from './supabase'

// MUST match the VAPID key PAIR in Netlify env (VAPID_PUBLIC_KEY / VAPID_PRIVATE_KEY).
// 2026-06-29: verified by deriving the public key from the env PRIVATE key (server-side
// vapid-check) — the env keypair's public half is THIS value (BEe60eAY…). The previous
// constant (BCY3v7JX…) did NOT pair with the env private key, so the server signed every
// push with a key no browser subscription recognized → silent 100% delivery failure
// (FCM/Apple reject the VAPID mismatch). Corrected to the env's actual public key; the
// self-heal below (ensureFreshSubscription + subscriptionKeyMatches) re-subscribes every
// existing browser to this key on next app load — nobody has to re-toggle anything.
export const VAPID_PUBLIC_KEY = 'BEe60eAYqt-I9tiXlB5lTsxEdySSXIx-I_fe0_6esH602tJW058E_5XDERNOfHK1clEDZNjW_qIjEOc-W67hJsU'

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)))
}

// Does an existing browser subscription's server key match our current VAPID key?
// A subscription created with a different (stale/wrong) key can never receive our
// pushes — it must be torn down and recreated.
function subscriptionKeyMatches(sub) {
  try {
    const buf = sub?.options?.applicationServerKey
    if (!buf) return false
    const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
    return b64 === VAPID_PUBLIC_KEY
  } catch { return false }
}

export async function subscribeToPush(userId) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null

  try {
    const reg = await navigator.serviceWorker.ready
    const existing = await reg.pushManager.getSubscription()
    // Reuse ONLY if it was created with our current key. Otherwise it's a dead
    // subscription (e.g. from the broken-key window) — drop it and re-subscribe.
    if (existing && subscriptionKeyMatches(existing)) {
      await saveSubscription(userId, existing)
      return existing
    }
    if (existing) { try { await existing.unsubscribe() } catch (e) {} }

    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    })
    await saveSubscription(userId, sub)
    return sub
  } catch (e) {
    console.error('Push subscribe error:', e)
    return null
  }
}

// Force a BRAND-NEW subscription. When a push endpoint expires the server gets a
// 410 and prunes the DB row, but the browser still hands the SAME dead subscription
// back from getSubscription() and its VAPID key still matches — so a plain
// subscribeToPush() just re-saves the dead endpoint and push stays broken (this is
// why "Turn on here" / the test could report success yet nothing ever arrived,
// Jason 2026-07-22). Tearing the browser sub down and re-subscribing mints a fresh
// endpoint. Use for EXPLICIT user actions (Enable / test-reheal), not the silent boot
// heal, so we don't churn endpoints on every load.
export async function resubscribeFresh(userId) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null
  try {
    const reg = await navigator.serviceWorker.ready
    const existing = await reg.pushManager.getSubscription()
    if (existing) { try { await existing.unsubscribe() } catch (e) {} }
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    })
    await saveSubscription(userId, sub)
    return sub
  } catch (e) {
    console.error('Push resubscribe error:', e)
    return null
  }
}

// Self-heal on every app load: if the user already granted permission, make sure
// their live subscription uses the current key (and exists in the DB). Repairs
// everyone stranded by the broken-key window WITHOUT them re-toggling anything.
// 2026-07-26: also close the dead-endpoint loop. The update flow can unregister the
// service worker (version-mismatch recovery / watchdog), which silently destroys the
// browser's push subscription — the DB row lives on pointing at a dead endpoint, every
// send 410s, and the plain heal below just re-saves the zombie (its VAPID key still
// matches). Now the server PRUNES the row on a 404/410 send (watch-together.js), and
// this boot heal notices the missing row and mints a brand-new endpoint. Together they
// self-repair within one app-open of a failed push ("pushes just stopped", Jason).
// Rotate a subscription this old even when it looks healthy. FCM/Apple can stop
// delivering to an endpoint that is still technically valid (never 410s), so the
// dead-endpoint prune never fires and the plain heal keeps re-saving the zombie —
// which is how Melissa went days with no pushes while every send "succeeded"
// server-side (Jason 2026-08-13). Minting a fresh endpoint on a slow cadence bounds
// that silent-rot window to ROTATE_AFTER_DAYS without churning endpoints every load.
const ROTATE_AFTER_DAYS = 14

export async function ensureFreshSubscription(userId) {
  if (!userId) return
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
  try {
    const { data } = await supabase.from('push_subscriptions')
      .select('user_id, updated_at').eq('user_id', userId).maybeSingle()
    if (!data) { await resubscribeFresh(userId); return }
    // Stored endpoint is older than the rotation window → mint a brand-new one
    // rather than reuse a possibly silently-dead endpoint.
    const ageMs = Date.now() - new Date(data.updated_at).getTime()
    if (Number.isFinite(ageMs) && ageMs > ROTATE_AFTER_DAYS * 86400000) {
      await resubscribeFresh(userId)
      return
    }
  } catch { /* connectivity blip — fall through to the normal heal */ }
  await subscribeToPush(userId)
}

async function saveSubscription(userId, sub) {
  await supabase.from('push_subscriptions').upsert({
    user_id: userId,
    subscription: JSON.parse(JSON.stringify(sub)),
    updated_at: new Date().toISOString(),
  }, { onConflict: 'user_id' })
}


export async function requestPermission() {
  if (!('Notification' in window)) return 'unsupported'
  if (Notification.permission === 'granted') return 'granted'
  if (Notification.permission === 'denied') return 'denied'
  const result = await Notification.requestPermission()
  return result
}
